/**
 * KPICard — single big-number tile used on dashboards. Tabular figures on
 * the value so the decimal point never shifts; uppercase eyebrow label
 * matches design-system.md §4 (H5 / eyebrow).
 */
import { Card } from './Card';
import { cn } from './cn';

export type DeltaTone = 'up' | 'down' | 'flat' | 'warn';

export interface KPICardProps {
  label: string;
  value: string;
  /** Optional short helper line below the value, e.g. "30d gemiddelde". */
  sub?: string;
  /** Optional delta string, e.g. "+2,4 pp". */
  delta?: string;
  deltaTone?: DeltaTone;
  /** Optional Tailwind class to colour the value (e.g. brand or status). */
  accent?: string;
  className?: string;
}

const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
  up: 'text-pass-700',
  down: 'text-fail-700',
  warn: 'text-warn-700',
  flat: 'text-fg-muted',
};

export function KPICard({
  label,
  value,
  sub,
  delta,
  deltaTone = 'flat',
  accent,
  className,
}: KPICardProps): JSX.Element {
  return (
    <Card className={cn('px-5 py-4', className)}>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">
        {label}
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <div
          className={cn(
            'text-2xl font-bold tabular-nums tracking-tight',
            accent ?? 'text-fg-primary',
          )}
        >
          {value}
        </div>
        {delta ? (
          <span className={cn('font-mono text-xs', DELTA_TONE_CLASS[deltaTone])}>{delta}</span>
        ) : null}
      </div>
      {sub ? <div className="mt-1 text-xs text-fg-muted">{sub}</div> : null}
    </Card>
  );
}
